import { isOfflineEnabled } from './isOffline';
import { loadOfflineDb, withOfflineDb } from './offlineDb';

function jsonResponse(body, status = 200) {
  return Promise.resolve(
    new Response(JSON.stringify(body), {
      status,
      headers: { 'Content-Type': 'application/json' },
    })
  );
}

function parseJsonBody(init) {
  try {
    const raw = init?.body;
    if (!raw) return null;
    if (typeof raw === 'string') return JSON.parse(raw);
    return raw;
  } catch {
    return null;
  }
}

function normalizeUrl(input) {
  try {
    if (typeof input === 'string') return input;
    if (input?.url) return String(input.url);
  } catch {}
  return String(input || '');
}

function normalizeMethod(init, input) {
  return String(init?.method || input?.method || 'GET').toUpperCase();
}

export function patchFetchForOffline() {
  if (!isOfflineEnabled()) return;
  const w = globalThis.window;
  if (typeof w === 'undefined' || typeof w.fetch !== 'function') return;
  if (w.fetch.__ssOfflinePatched) return;

  const originalFetch = w.fetch.bind(w);

  async function handler(input, init = {}) {
    const url = normalizeUrl(input);
    const method = normalizeMethod(init, input);

    // Marketing tracking endpoints: no-op
    if (url.startsWith('/api/marketing/demo-click/register/')) {
      return jsonResponse({ ok: true });
    }

    // Schedule admin endpoints used by DemoAdmin
    if (url === '/functions/schedule/admin/list' && method === 'GET') {
      const db = loadOfflineDb();
      const schedule = db.schedule || { bookings: [], closures: [] };
      return jsonResponse({ bookings: schedule.bookings || [], closures: schedule.closures || [] });
    }

    if (url === '/functions/schedule/admin/update' && method === 'POST') {
      const body = parseJsonBody(init) || {};
      const id = body.id;
      withOfflineDb((db) => {
        const schedule = db.schedule || { bookings: [], closures: [] };
        const bookings = Array.isArray(schedule.bookings) ? schedule.bookings : [];
        const nextBookings = bookings.map((b) => (Number(b?.id) === Number(id) ? { ...b, ...body } : b));
        return { ...db, schedule: { ...schedule, bookings: nextBookings } };
      });
      return jsonResponse({ ok: true });
    }

    if (url === '/functions/schedule/admin/cancel' && method === 'POST') {
      const body = parseJsonBody(init) || {};
      const id = body.id;
      withOfflineDb((db) => {
        const schedule = db.schedule || { bookings: [], closures: [] };
        const bookings = Array.isArray(schedule.bookings) ? schedule.bookings : [];
        const nextBookings = bookings.map((b) => (Number(b?.id) === Number(id) ? { ...b, status: 'cancelled' } : b));
        return { ...db, schedule: { ...schedule, bookings: nextBookings } };
      });
      return jsonResponse({ ok: true });
    }

    if ((url === '/functions/schedule/admin/close' || url === '/functions/schedule/admin/open') && method === 'POST') {
      // Keep it simple: accept and return ok
      return jsonResponse({ ok: true });
    }

    // Public schedule endpoints used on marketing landing
    if (url.startsWith('/functions/schedule/availability') && method === 'GET') {
      // Provide a few fake slots
      const now = new Date();
      const slots = Array.from({ length: 6 }).map((_, i) => {
        const d = new Date(now.getTime() + (i + 1) * 24 * 60 * 60 * 1000);
        d.setHours(10, 0, 0, 0);
        return d.toISOString();
      });
      return jsonResponse({ slots });
    }

    if (url === '/functions/schedule/book' && method === 'POST') {
      const body = parseJsonBody(init) || {};
      withOfflineDb((db) => {
        const schedule = db.schedule || { bookings: [], closures: [] };
        const bookings = Array.isArray(schedule.bookings) ? schedule.bookings : [];
        const maxId = bookings.reduce((m, r) => Math.max(m, Number(r?.id) || 0), 0);
        const booking = { id: maxId + 1, status: 'new', createdAt: new Date().toISOString(), ...body };
        return { ...db, schedule: { ...schedule, bookings: [booking, ...bookings] } };
      });
      return jsonResponse({ ok: true });
    }

    return originalFetch(input, init);
  }

  handler.__ssOfflinePatched = true;
  w.fetch = handler;
  w.fetch.__ssOfflinePatched = true;
}

// Side-effect import convenience
patchFetchForOffline();
